// Gráfico central del Dashboard directivo — "Cumplimiento y producción".
//
// Barras (unidades, eje izq): Producción · Programación · Meta capacidad.
// Líneas (%, eje der): Cumplimiento cliente (accent, LA prioridad) ·
// Cumplimiento capacidad (info, punteada). Colores de la paleta establecida
// del proyecto (PLANTA_COLORS de D2: accent/info/warn/ok/crit).
//
// Etiquetas (feedback responsable 2026-07-04): los % de las líneas SIEMPRE
// visibles fuera de la serie (cliente arriba, capacidad abajo); el valor de la
// barra de producción va DENTRO de la columna, en vertical.
//
// Las LEYENDAS SON FILTROS (toggle de series): chip con swatch de color;
// apagado = atenuado. Se usa `hide` de Recharts (la serie no se desmonta).
// Cliente/Programación quedan deshabilitadas solo cuando el programa no aplica
// al corte (granularidad horaria o filtro de turno — el programa es diario).

import { useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '../../../lib/utils';
import { LabelValorMeta, LabelValorProduccion } from './chartLabels';

export interface PuntoChart {
  bucket: string;
  label: string;
  produccion: number;
  meta: number;
  programa: number | null;
  cliente_pct: number | null;
  capacidad_pct: number | null;
  /** Nivel de servicio REAL (justificadas causa cliente, tope 100). */
  real_pct: number | null;
  /** % a ETIQUETAR (null cuando chocaría con la otra línea: gana el más alto). */
  cliente_label: number | null;
  capacidad_label: number | null;
}

type SerieKey = 'produccion' | 'programa' | 'meta' | 'cliente' | 'capacidad' | 'real';

// Cumplimiento cliente = la prioridad: en fg (blanca en tema oscuro, negra en
// claro — pedido del responsable). Capacidad en info (azul), punteada.
const SERIES: Array<{ key: SerieKey; label: string; tipo: 'bar' | 'line'; color: string }> = [
  { key: 'cliente', label: 'Cumplimiento cliente', tipo: 'line', color: 'var(--color-fg)' },
  { key: 'real', label: 'Cumplimiento REAL', tipo: 'line', color: 'var(--color-ok)' },
  { key: 'capacidad', label: 'Cumplimiento capacidad', tipo: 'line', color: 'var(--color-info)' },
  { key: 'produccion', label: 'Producción', tipo: 'bar', color: 'var(--color-fg-tertiary)' },
  { key: 'programa', label: 'Programación', tipo: 'bar', color: 'var(--color-border-stronger)' },
  {
    key: 'meta',
    label: 'Meta capacidad instalada',
    tipo: 'bar',
    color: 'var(--color-border-strong)',
  },
];

function miles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(Math.round(n));
}

function pctLabel(v: unknown): string {
  return v === null || v === undefined ? '' : `${Math.round(Number(v))}%`;
}

export function CumplimientoProduccionChart({
  data,
  conPrograma,
}: {
  data: PuntoChart[];
  /** false cuando el programa cliente no aplica (granularidad horaria o turno). */
  conPrograma: boolean;
}) {
  // Apagadas por defecto: meta como barra (la línea de capacidad ya la cuenta).
  const [ocultas, setOcultas] = useState<Set<SerieKey>>(() => new Set(['meta']));
  const toggle = (k: SerieKey) =>
    setOcultas((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const deshabilitada = (k: SerieKey) =>
    (k === 'cliente' || k === 'programa' || k === 'real') && !conPrograma;
  const visible = (k: SerieKey) => !ocultas.has(k) && !deshabilitada(k);

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-fg-tertiary">
        Sin capturas en el período seleccionado
      </div>
    );
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart
          data={data}
          margin={{ top: 18, right: 0, left: -8, bottom: 0 }}
          barGap={3}
          barCategoryGap="26%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: 'var(--color-fg-secondary)' }}
            axisLine={{ stroke: 'var(--color-border)' }}
            tickLine={false}
          />
          <YAxis
            yAxisId="unds"
            tickFormatter={(v) => miles(Number(v))}
            tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <YAxis
            yAxisId="pct"
            orientation="right"
            domain={[0, 130]}
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
            axisLine={false}
            tickLine={false}
            width={40}
          />
          <Tooltip
            cursor={{ fill: 'var(--color-surface-overlay)' }}
            contentStyle={{
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(v, name) => {
              const key = String(name);
              if (
                key === 'Cumplimiento cliente' ||
                key === 'Cumplimiento capacidad' ||
                key === 'Cumplimiento REAL'
              )
                return [`${Math.round(Number(v))}%`, key];
              return [`${miles(Number(v))} paq`, key];
            }}
          />
          <Bar
            yAxisId="unds"
            dataKey="produccion"
            name="Producción"
            fill="var(--color-fg-tertiary)"
            radius={[2, 2, 0, 0]}
            maxBarSize={28}
            hide={!visible('produccion')}
          >
            {/* Valor adentro si cabe; arriba de la barra si la columna es bajita */}
            <LabelList dataKey="produccion" content={LabelValorProduccion} />
          </Bar>
          <Bar
            yAxisId="unds"
            dataKey="programa"
            name="Programación"
            fill="var(--color-border-stronger)"
            radius={[2, 2, 0, 0]}
            maxBarSize={28}
            hide={!visible('programa')}
          />
          <Bar
            yAxisId="unds"
            dataKey="meta"
            name="Meta capacidad instalada"
            fill="var(--color-border-strong)"
            radius={[2, 2, 0, 0]}
            maxBarSize={28}
            hide={!visible('meta')}
          >
            <LabelList dataKey="meta" content={LabelValorMeta} />
          </Bar>
          <Line
            yAxisId="pct"
            dataKey="cliente_pct"
            name="Cumplimiento cliente"
            stroke="var(--color-fg)"
            strokeWidth={2}
            dot={{ r: 3, fill: 'var(--color-fg)', strokeWidth: 0 }}
            connectNulls
            hide={!visible('cliente')}
          >
            {/* % del cliente por fuera; cliente_label ya viene depurado de
                choques con la línea de capacidad (gana el valor más alto) */}
            <LabelList
              dataKey="cliente_label"
              position="top"
              offset={8}
              formatter={pctLabel}
              style={{ fontSize: 10.5, fill: 'var(--color-fg)', fontWeight: 700 }}
            />
          </Line>
          <Line
            yAxisId="pct"
            dataKey="real_pct"
            name="Cumplimiento REAL"
            stroke="var(--color-ok)"
            strokeWidth={1.5}
            dot={{ r: 2.5, fill: 'var(--color-ok)', strokeWidth: 0 }}
            connectNulls
            hide={!visible('real')}
          />
          <Line
            yAxisId="pct"
            dataKey="capacidad_pct"
            name="Cumplimiento capacidad"
            stroke="var(--color-info)"
            strokeWidth={1.5}
            strokeDasharray="5 4"
            dot={{ r: 2.5, fill: 'var(--color-info)', strokeWidth: 0 }}
            connectNulls
            hide={!visible('capacidad')}
          >
            <LabelList
              dataKey="capacidad_label"
              position="bottom"
              offset={8}
              formatter={pctLabel}
              style={{ fontSize: 10, fill: 'var(--color-info)', fontWeight: 500 }}
            />
          </Line>
        </ComposedChart>
      </ResponsiveContainer>

      {/* Leyendas-filtro */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {SERIES.map((s) => {
          const off = deshabilitada(s.key);
          const on = visible(s.key);
          return (
            <button
              key={s.key}
              type="button"
              disabled={off}
              onClick={() => toggle(s.key)}
              title={
                off
                  ? 'El programa del cliente es diario: no aplica por hora ni por turno'
                  : on
                    ? 'Clic para ocultar esta serie'
                    : 'Clic para mostrar esta serie'
              }
              className={cn(
                'inline-flex items-center gap-2 rounded border px-2.5 py-1 text-[11px] transition-all',
                // Tres estados distinguibles: NO APLICA (tachado, no clickeable)
                // ≠ apagado (atenuado, clic lo prende) ≠ prendido.
                off
                  ? 'cursor-not-allowed border-border/60 text-fg-disabled line-through opacity-40'
                  : on
                    ? 'border-border-strong bg-surface-overlay text-fg'
                    : 'border-border text-fg-tertiary opacity-60 hover:opacity-90',
              )}
            >
              {s.tipo === 'line' ? (
                <span
                  className="inline-block h-0 w-4 border-t-2"
                  style={{
                    borderColor: off ? 'var(--color-fg-disabled)' : s.color,
                    borderStyle: s.key === 'capacidad' ? 'dashed' : 'solid',
                  }}
                />
              ) : (
                <span
                  className="inline-block size-2.5 rounded-[2px]"
                  style={{ background: off ? 'var(--color-fg-disabled)' : s.color }}
                />
              )}
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
